import "./borrowerDashboardstyle.css";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import {
  useActiveOffersQuery,
  useAllApplicationsQuery,
  useGetUserQuery,
  useAllApplicationsCountQuery
} from "../../API/Dashboard";
import useDebounce from "@hooks/useDebounce";
import { defaultParams } from "@utils/global.constants";
import storage from "@utils/storage";
import { useSocket } from "@utils/socket/socketContext";
import { createApplication } from "../../API/Applications";
import {
  APPLICATION_REDIRECT_PATHS,
  getApplicationRedirectUrl,
} from "@utils/redirectUrl";
import { failed, success } from "@components/Common/Toastify";
import purplesvg from "../../Assets/Images/purple-svg.png";
import greensvg from "../../Assets/Images/green-svg.png";
import yellowsvg from "../../Assets/Images/yellow-svg.png";
import redsvg from "../../Assets/Images/red-svg.png";
import groupedcircles from "../../Assets/Images/circle-grp.png";
import { useFormik } from "formik";
import axios from "axios";
import envConfig from "../../environment";
import { Country } from 'country-state-city';
import { useGetWalletQuery } from "../../API/Transaction";
import { getAnonymousLenderLabel } from "@utils/lenderDisplay";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import dummyImg from "../../Assets/Images/dummy-img.png";
const countries = Country.getAllCountries();

type Offer = {
  offers: any[]
  uuid: string;
  lenderId: string;
  title: string;
  loanAmount: string;
  interestRate: string;
  loanDuration: string;
  termsAndConditions: string;
  status: string;
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  applications: any[];
};


function BorrowerDashboard() {
  const navigate = useNavigate();
  const Id = storage.getUser();
  const userId = Id.user.userId;
  const [btnLoading, setBtnLoading] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { socket } = useSocket();
  const [params, setParams] = useState(defaultParams);
  const debouncedSearch = useDebounce(params.search, 500);
  const user = storage.getUser();
  const [onboardingUrl, setOnboardingUrl] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const borrowerId = user?.user?.userId;
  const { isLoading, isFetching, data, refetch, error, isError } =
    useActiveOffersQuery({
      params: { ...params, search: debouncedSearch },
    });
  const offers: Offer[] = data?.offers || [];
  const latestOffer = data?.latestActiveOffer || null;
  const latestImage = latestOffer?.lender?.profileImage || null;
  const [profileImage, setProfileImage] = useState<string | null>(null);

  const [selectedSortLabel, setSelectedSortLabel] = useState("Newest First");
  const {
    data: userData,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetUserQuery({ userId, params: {} });
  const {
    data: applicationsData,
    isLoading: isAppsLoading,
    isError: isAppsError,
  } = useAllApplicationsQuery({ userId, params: {} });

  const appliedOfferIds = new Set(applicationsData?.applications?.map((app: any) => app.offerId));

  const {
    isLoading: isWalletLoading,
    isFetching: isWalletFetching,
    data: walletData,
    error: walletError,
    isError: isWalletError,
  } = useGetWalletQuery(userId, params);
  const walletAmount: number = walletData?.totalAmount || 0;

  const {
    isLoading: isCountLoading,
    isFetching: isCountFetching,
    data: applicationCounts,
    refetch: refetchCounts,
    error: countError,
    isError: isCountError,
  } = useAllApplicationsCountQuery({
    userId,
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      borrowerId: borrowerId ?? "",
      offerId: selectedOffer?.uuid ?? "",
      firstName: userData?.firstName || "",
      lastName: userData?.lastName || "",
      email: userData?.email || "",
      phoneNumber: userData?.phoneNumber || "",
      pinCode: userData?.pinCode || "",
      address: userData?.address || "",
      country: userData?.country,
      state: userData?.state || "",
      city: userData?.city || "",
      region: userData?.region || "",
    },
    validationSchema: yup.object({
      borrowerId: yup.string().required("Borrower ID is required."),
      offerId: yup.string().required("Offer ID is required."),

      firstName: yup
        .string()
        .matches(/^[a-zA-Z\s'-]+$/, "First name can only contain letters, spaces, apostrophes, and hyphens.")
        .min(2, "First name must be at least 2 characters.")
        .max(50, "First name must be at most 50 characters.")
        .required("First name is required."),

      lastName: yup
        .string()
        .matches(/^[a-zA-Z\s'-]+$/, "Last name can only contain letters, spaces, apostrophes, and hyphens.")
        .min(2, "Last name must be at least 2 characters.")
        .max(50, "Last name must be at most 50 characters.")
        .required("Last name is required."),

      email: yup
        .string()
        .email("Enter a valid email address.")
        .required("Email is required."),

      phoneNumber: yup
        .string()
        .required("Phone number is required")
        .matches(/^\+\d{6,15}$/, "Please enter valid phone number. "),

      pinCode: yup
        .string()
        .matches(/^[1-9][0-9]{3,5}$/, "Enter a valid pincode")
        .required("Pincode is required"),
      address: yup
        .string()
        .min(5, "Address must be at least 5 characters.")
        .max(200, "Address must be at most 200 characters.")
        .required("Address is required."),

      country: yup
        .string()
        .required("Country is required."),

      state: yup
        .string()
        .required("State is required."),

      city: yup
        .string()
        .required("City is required."),

      region: yup
        .string()
        .min(3, "Region must be at least 3 characters.")
        .max(200, "Region must be at most 200 characters.")
        .required("Region is required."),
    })
    ,
    onSubmit: async (values) => {
      setBtnLoading(true);
      try {
        const redirectUrl = getApplicationRedirectUrl(
          APPLICATION_REDIRECT_PATHS.dashboard
        );

        const payload = { ...values, redirectUrl };
        const res = await createApplication(payload);
        const lenderId = res?.data?.offerData?.lenderId;
        const applicationId = res?.data?.applicationId;
        const offerData = res?.data?.offerData;

        const notificationMessage = `New loan application received from ${user?.user?.firstName} for the Offer: ${offerData?.title}.`;
        const notificationTitle = `New Application for "${offerData?.title}"`;

        // ✅ 1. Save notification via API first
        try {
          await axios.post(
            `${envConfig.apiUrl}/notification/create-offer-notification`,
            {
              senderId: user?.user?.userId,
              senderType: "borrower",
              receiverType: "lender",
              receiverId: lenderId,
              title: notificationTitle,
              notification: notificationMessage,
              isGlobal: false,
            },
            {
              headers: {
                Authorization: `Bearer ${storage.getToken()}`,
              },
            }
          );
        } catch (apiErr) {
          console.error("Failed to save application notification:", apiErr);
        }

        // ✅ 2. Emit socket event after DB notification is saved
        if (socket?.connected && lenderId) {
          socket.emit("create application", {
            lenderId,
            borrowerId: user?.uuid,
            applicationId,
            details: values,
            notificationMessage,
          });
        }

        // ✅ 3. Continue with success UI
        success(res?.data?.message || "Application submitted!");
        setIsModalOpen(false);
        navigate("/app/submitted-applications");
      } catch (err: any) {
        const errorData = err?.response?.data;
        if (errorData?.needsOnboarding && errorData?.onboardingUrl) {
          toast("You need to complete Stripe onboarding before applying.");
          setOnboardingUrl(errorData.onboardingUrl);
          return;
        }
        toast.error(errorData?.message || err.message || "Failed to submit application");
      } finally {
        setBtnLoading(false);

      };
    },


  });
  const approved =
    applicationCounts?.approved?.count || 0;

  const underReview =
    applicationCounts?.underReview?.count || 0;

  const submitted =
    applicationCounts?.submitted?.count || 0;

  const rejected =
    applicationCounts?.rejected?.count || 0;

  const approvedAmount =
    applicationCounts?.approved?.totalAmount || 0;

  const underReviewAmount =
    applicationCounts?.underReview?.totalAmount || 0;

  const submittedAmount =
    applicationCounts?.submitted?.totalAmount || 0;

  const rejectedAmount =
    applicationCounts?.rejected?.totalAmount || 0;


  const summaries = [
    { count: approved, label: "Approved", icon: greensvg, path: "/app/approved-applications", amount: approvedAmount },
    { count: underReview, label: "Under Review", icon: yellowsvg, path: "/app/underReview-applications", amount: underReviewAmount },
    { count: submitted, label: "Submitted", icon: purplesvg, path: "/app/submitted-applications", amount: submittedAmount },
    { count: rejected, label: "Rejected", icon: redsvg, path: "/app/rejected-applications", amount: rejectedAmount },
  ];

  useEffect(() => {
    refetch();
  }, [debouncedSearch, refetch]);

  useEffect(() => {
    if (latestImage) {
      setProfileImage(latestImage);
    }
  }, [latestImage]);

  return (
    <div>
      <div className="content dashboard ">
        <div className="content-wrapper borrower-dashboard-section container-fluid p-0">
          <div className="row mb-4">
            <div className="col-xl-6 wall-main">
              <div className="wallet-card d-flex w-100 h-100">
                {/* <div className="lendriva-box-block position-relative overflow-hidden">
                  <div className="inner-box-content h-100 d-flex flex-column justify-content-between">
                    <div className="content-upper">
                      <h6 className="m-0">Lendriva</h6>
                      <p className="m-0">Lender Account</p>
                    </div>
                    <div className="content-lower">
                      <p className="m-0">Wallet Holder</p>
                      <h5 className="m-0"><span>  {user?.user?.firstName ? user.user?.firstName.charAt(0).toUpperCase() + user.user.firstName.slice(1) : "Andrew"}</span> <span> {user.user?.lastName ? user.user.lastName.charAt(0).toUpperCase() + user.user.lastName.slice(1) : "Lender"}</span></h5>

                    </div>
                  </div>

                  <div className="grouped-ellipse position-absolute">
                    <img src={ellipses} alt="" className="img-fluid" />
                  </div>
                  <div className="wallet borrower-db position-absolute d-block">
                    <img src={wallet} alt="" className="img-fluid" />
                  </div>
                </div> */}
                <div className="wallet-content d-flex flex-column">
                  <div className="flex-grow-1">
                    <h3 className="m-0">Smart Lending About timing.</h3>
                    <h3 className="m-0">Check out new Opportunities Bellow.</h3>
                  </div>
                  {/* <h2 className="m-0">£{walletAmount}</h2> */}
                  {/* <p className="m-0">Wallet Balance</p> */}
                  <div className="btn-wallet d-flex gap-3">
                    <button
                      onClick={() => navigate("/app/add-money")}
                      className="btn-white"
                    >
                      Add Funds
                    </button>
                    <button
                      onClick={() => navigate("/app/withdraw-money")}
                      className="btn-blue"
                    >
                      Withdraw
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-6">
              <div className="row">
                <div className="col-lg-6 application-blk-main">
                  <div className="application-blk d-flex flex-column justify-content-between align-items-center h-100">
                    <img src={dummyImg} alt="" className="profile-docs" />
                    <h6 className=" text-center">Applications</h6>
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <Link to="/app/browse-offers" style={{ textDecoration: "none" }}>
                        <button><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_123_715)">
                            <rect width="24" height="24" fill="#FBFCFF" />
                            <path d="M12.0469 0C5.45578 0 0 5.36203 0 11.9531C0 18.5442 5.45578 24 12.0469 24C18.638 24 24 18.5442 24 11.9531C24 5.36203 18.638 0 12.0469 0ZM18.375 13.3594H13.4531V18.375C13.4531 19.1503 12.8217 19.7812 12.0469 19.7812C11.2716 19.7812 10.6406 19.1503 10.6406 18.375V13.3594H5.625C4.84969 13.3594 4.21875 12.7284 4.21875 11.9531C4.21875 11.1778 4.84969 10.5469 5.625 10.5469H10.6406V5.625C10.6406 4.84969 11.2716 4.21875 12.0469 4.21875C12.8217 4.21875 13.4531 4.84969 13.4531 5.625V10.5469H18.375C19.1498 10.5469 19.7812 11.1778 19.7812 11.9531C19.7812 12.7284 19.1498 13.3594 18.375 13.3594Z" fill="#202224" />
                          </g>
                          <defs>
                            <clipPath id="clip0_123_715">
                              <rect width="24" height="24" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                        </button>
                      </Link>
                      <Link to="/app/wallet" style={{ textDecoration: "none" }}>
                        <button>
                          <svg width="24" height="24" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.71547 2.12584C5.71547 1.37197 6.32879 0.758652 7.08266 0.758652H7.55581L7.41308 0.361895C7.33283 0.13877 7.12255 0 6.89844 0C6.83635 0 6.77318 0.0106641 6.71144 0.0331406L1.9303 1.77332C1.64606 1.87679 1.49977 2.19133 1.60385 2.47535L1.85647 3.1649H5.71547V2.12584Z" fill="#434343" />
                            <path d="M7.08203 1.57896C6.77999 1.57896 6.53516 1.8238 6.53516 2.12584V3.1649H11.3203V2.12584C11.3203 1.8238 11.0755 1.57896 10.7734 1.57896H7.08203Z" fill="#434343" />
                            <path d="M12.7695 11.0536H10.8555C9.72467 11.0536 8.80469 10.1336 8.80469 9.00279C8.80469 7.87199 9.72467 6.95201 10.8555 6.95201H12.7695V5.07896C12.7695 4.47491 12.2798 3.98521 11.6758 3.98521H1.09375C0.489699 3.98521 0 4.47491 0 5.07896V12.9062C0 13.5103 0.489699 14 1.09375 14H11.6758C12.2798 14 12.7695 13.5103 12.7695 12.9062V11.0536Z" fill="#202224" />
                            <path d="M13.5898 7.77232H10.8555C10.177 7.77232 9.625 8.32431 9.625 9.00279C9.625 9.68127 10.177 10.2333 10.8555 10.2333H13.5898C13.816 10.2333 14 10.0493 14 9.8231V8.18248C14 7.95632 13.816 7.77232 13.5898 7.77232ZM10.8555 9.41295C10.629 9.41295 10.4453 9.22931 10.4453 9.00279C10.4453 8.77628 10.629 8.59264 10.8555 8.59264C11.082 8.59264 11.2656 8.77628 11.2656 9.00279C11.2656 9.22931 11.082 9.41295 10.8555 9.41295Z" fill="#202224" />
                          </svg>

                        </button>
                      </Link>
                      <button>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_123_726)">
                            <rect width="24" height="24" fill="#FBFCFF" />
                            <path d="M11.8295 11.5609C13.4177 11.5609 14.793 10.9912 15.9167 9.86733C17.0405 8.74362 17.6101 7.36868 17.6101 5.78025C17.6101 4.19237 17.0405 2.81725 15.9166 1.69317C14.7927 0.569639 13.4175 0 11.8295 0C10.241 0 8.86609 0.569639 7.74237 1.69335C6.61865 2.81707 6.04883 4.19218 6.04883 5.78025C6.04883 7.36868 6.61865 8.7438 7.74255 9.86751C8.86646 10.991 10.2416 11.5609 11.8295 11.5609Z" fill="#202224" />
                            <path d="M21.9435 18.4547C21.9111 17.9871 21.8456 17.477 21.7491 16.9383C21.6517 16.3956 21.5262 15.8825 21.3761 15.4136C21.221 14.9289 21.0101 14.4502 20.7493 13.9916C20.4787 13.5155 20.1608 13.1009 19.8041 12.7598C19.4312 12.403 18.9745 12.116 18.4464 11.9067C17.9202 11.6985 17.337 11.5931 16.7131 11.5931C16.4681 11.5931 16.2312 11.6936 15.7736 11.9915C15.492 12.1752 15.1626 12.3876 14.7949 12.6225C14.4805 12.8228 14.0546 13.0105 13.5286 13.1804C13.0153 13.3465 12.4942 13.4307 11.9799 13.4307C11.4655 13.4307 10.9446 13.3465 10.4308 13.1804C9.90527 13.0107 9.47937 12.823 9.16534 12.6227C8.80115 12.39 8.47156 12.1775 8.18573 11.9913C7.7287 11.6934 7.49158 11.5929 7.24658 11.5929C6.62256 11.5929 6.03955 11.6985 5.51349 11.9069C4.98578 12.1158 4.52893 12.4028 4.15558 12.76C3.79907 13.1013 3.48102 13.5157 3.21075 13.9916C2.9502 14.4502 2.73926 14.9287 2.58398 15.4137C2.43402 15.8827 2.30859 16.3956 2.21118 16.9383C2.11469 17.4762 2.04913 17.9865 2.01672 18.4553C1.98486 18.9145 1.96875 19.3911 1.96875 19.8725C1.96875 21.1253 2.367 22.1395 3.15234 22.8875C3.92798 23.6256 4.95428 24.0001 6.20233 24.0001H17.7585C19.0065 24.0001 20.0325 23.6258 20.8083 22.8875C21.5938 22.1401 21.9921 21.1257 21.9921 19.8723C21.9919 19.3888 21.9756 18.9118 21.9435 18.4547Z" fill="#202224" />
                          </g>
                          <defs>
                            <clipPath id="clip0_123_726">
                              <rect width="24" height="24" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>

                      </button>
                      {/* <button>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="24" height="24" fill="#FBFCFF" />
                          <path d="M11.9999 18.8403C11.5698 18.8403 11.1397 18.6761 10.8118 18.3483L0.492339 8.02874C-0.164113 7.37229 -0.164113 6.30796 0.492339 5.65178C1.14853 4.99559 2.21264 4.99559 2.86914 5.65178L11.9999 14.7831L21.1308 5.6521C21.7872 4.99591 22.8512 4.99591 23.5074 5.6521C24.1641 6.30828 24.1641 7.37261 23.5074 8.02906L13.1881 18.3486C12.86 18.6765 12.4299 18.8403 11.9999 18.8403Z" fill="#202224" />
                        </svg>

                      </button> */}
                      <button onClick={() => setOpen((prev) => !prev)}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="24" height="24" fill="#FBFCFF" />
                          <path
                            d="M11.9999 18.8403C11.5698 18.8403 11.1397 18.6761 10.8118 18.3483L0.492339 8.02874C-0.164113 7.37229 -0.164113 6.30796 0.492339 5.65178C1.14853 4.99559 2.21264 4.99559 2.86914 5.65178L11.9999 14.7831L21.1308 5.6521C21.7872 4.99591 22.8512 4.99591 23.5074 5.6521C24.1641 6.30828 24.1641 7.37261 23.5074 8.02906L13.1881 18.3486C12.86 18.6765 12.4299 18.8403 11.9999 18.8403Z"
                            fill="#202224"
                          />
                        </svg>
                        {open && (
                          <ul
                            className="dropdown-menu show position-absolute mt-2 shadow-sm rounded"
                            style={{
                              minWidth: "180px",
                              backgroundColor: "#fff",
                              border: "2px solid #e5e7eb",
                              zIndex: 1050,
                            }}
                          >
                            <li>
                              <Link
                                to="/app/submitted-applications"
                                className="dropdown-item"
                                onClick={() => setOpen(false)}
                              >
                                Submitted
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/app/underReview-applications"
                                className="dropdown-item"
                                onClick={() => setOpen(false)}
                              >
                                Under Review
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/app/approved-applications"
                                className="dropdown-item"
                                onClick={() => setOpen(false)}
                              >
                                Approved
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/app/rejected-applications"
                                className="dropdown-item"
                                onClick={() => setOpen(false)}
                              >
                                Rejected
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/app/completed-applications"
                                className="dropdown-item"
                                onClick={() => setOpen(false)}
                              >
                                Completed
                              </Link>
                            </li>
                          </ul>
                        )}
                      </button>


                      {/* <details className="relative inline-block">
                        <summary className="flex items-center cursor-pointer list-none">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="24" height="24" fill="#FBFCFF" />
                            <path d="M11.9999 18.8403C11.5698 18.8403 11.1397 18.6761 10.8118 18.3483L0.492339 8.02874C-0.164113 7.37229 -0.164113 6.30796 0.492339 5.65178C1.14853 4.99559 2.21264 4.99559 2.86914 5.65178L11.9999 14.7831L21.1308 5.6521C21.7872 4.99591 22.8512 4.99591 23.5074 5.6521C24.1641 6.30828 24.1641 7.37261 23.5074 8.02906L13.1881 18.3486C12.86 18.6765 12.4299 18.8403 11.9999 18.8403Z" fill="#202224" />
                          </svg>
                        </summary>

                        <ul className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg">
                          <li><a href="/submitted" className="block px-4 py-2 hover:bg-gray-100">Submitted</a></li>
                          <li><a href="/underreview" className="block px-4 py-2 hover:bg-gray-100">Under Review</a></li>
                          <li><a href="/approved" className="block px-4 py-2 hover:bg-gray-100">Approved</a></li>
                          <li><a href="/rejected" className="block px-4 py-2 hover:bg-gray-100">Rejected</a></li>
                          <li><a href="/completed" className="block px-4 py-2 hover:bg-gray-100">Completed</a></li>
                        </ul>
                      </details> */}


                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="offer-bkl h-100">
                    <Link to="/app/browse-offers" style={{ textDecoration: "none" }}>
                      <h4>Active Offer
                        <span className="ms-2">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_123_741)">
                              <rect width="16" height="16" transform="translate(0 16) rotate(-90)" fill="#FBFCFF" />
                              <path d="M12.5599 8.00001C12.5599 8.28676 12.4504 8.57348 12.2319 8.7921L5.35219 15.6717C4.91455 16.1094 4.205 16.1094 3.76755 15.6717C3.33009 15.2343 3.33009 14.5249 3.76755 14.0872L9.85509 8.00001L3.76776 1.91278C3.3303 1.47515 3.3303 0.765813 3.76776 0.32839C4.20522 -0.109457 4.91477 -0.109457 5.3524 0.32839L12.2321 7.20792C12.4507 7.42665 12.5599 7.71337 12.5599 8.00001Z" fill="#202224" />
                            </g>
                            <defs>
                              <clipPath id="clip0_123_741">
                                <rect width="16" height="16" fill="white" transform="translate(0 16) rotate(-90)" />
                              </clipPath>
                            </defs>
                          </svg>
                        </span>
                      </h4>
                    </Link>
                    {latestOffer ? (
                      <>
                        <div className="d-flex gap-3 mb-3">
                          {profileImage ? (
                            <img
                              src={`${envConfig.photoUrl}/public/profileImage/${profileImage}`}
                              alt="Profile"
                              className="name-initials-box rounded-full object-cover w-full h-full"
                            />
                          ) : (
                            <div className="applicant-name-icon d-flex align-items-center justify-content-center">
                              <span className="text-capitalize">{latestOffer?.lender?.fullName?.[0] || "U"}</span>
                              <span className="text-lowercase">{latestOffer?.lender?.fullName?.split(" ")[1]?.[0] || "N"}</span>
                            </div>
                          )}
                          {/* <img src={dummyImg} alt="" className="profile-docs-two" /> */}

                          <div>
                            <p className="name-blk">{latestOffer?.lender?.fullName.charAt(0).toUpperCase() + latestOffer?.lender?.fullName.slice(1)}</p>
                            <p className="designation-blk">{latestOffer?.title}</p>
                          </div>
                        </div>
                        <div>
                          <div className="d-flex justify-content-between gap-px">
                            <p className="title-blk">Title:</p>
                            <p className="name-title-blk">{latestOffer?.title}</p>
                          </div>
                          <div className="d-flex justify-content-between gap-px">
                            <p className="title-blk">Loan Amount:</p>
                            <p className="name-title-blk">£ {latestOffer?.loanAmount}</p>
                          </div>
                          <div className="d-flex justify-content-between gap-px">
                            <p className="title-blk">Duration:</p>
                            <p className="name-title-blk">{latestOffer?.loanDuration} Month</p>
                          </div>
                          <div className="d-flex justify-content-between">
                            <p className="title-blk">Loan Interest:</p>
                            <p className="name-title-blk">{latestOffer?.interestRate}%</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center py-4">
                        <p>No active offer found.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row db-row m-0">
            {summaries.map((summary, index) => {
              const isLast = index === summaries.length - 1;
              return (
                <div
                  key={summary.label}
                  className="col-lg-3 mb-2"
                  onClick={() => navigate(summary.path)}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    className={`offer-boxes h-100 ${isLast ? "border-left-last" : ""}`}
                  >
                    <div className="offer-inner-wrapper d-flex flex-column justify-content-between h-100">
                      <div className="offer-upper-infobox">
                        <p className="m-0">{summary.label}</p>
                        <h6 className="m-0">{summary.count}</h6>
                      </div>
                      <div className="offer-lower-infobox-one">
                        £ {summary.amount}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <h5 className="heading-two mt-4 m-0 ofr-aaplicatn">Active Offers</h5>

          <div className="outer-box-wrapper-two">
            <div className="d-flex justify-content-between align-items-center title-dropdown-box borowwer-content mb-3">
              <div className="input-btn-fields d-flex align-items-center">
                <div className="search-box">
                  <div className="input-search position-relative">
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search"
                      value={params.search}
                      onChange={(e) =>
                        setParams((prev) => ({ ...prev, search: e.target.value }))
                      }
                    />
                    <div className="search-icon position-absolute">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13 13L9 9M1 5.66667C1 6.2795 1.12071 6.88634 1.35523 7.45252C1.58975 8.01871 1.93349 8.53316 2.36683 8.9665C2.80018 9.39984 3.31462 9.74358 3.88081 9.97811C4.447 10.2126 5.05383 10.3333 5.66667 10.3333C6.2795 10.3333 6.88634 10.2126 7.45252 9.97811C8.01871 9.74358 8.53316 9.39984 8.9665 8.9665C9.39984 8.53316 9.74358 8.01871 9.97811 7.45252C10.2126 6.88634 10.3333 6.2795 10.3333 5.66667C10.3333 5.05383 10.2126 4.447 9.97811 3.88081C9.74358 3.31462 9.39984 2.80018 8.9665 2.36683C8.53316 1.93349 8.01871 1.58975 7.45252 1.35523C6.88634 1.12071 6.2795 1 5.66667 1C5.05383 1 4.447 1.12071 3.88081 1.35523C3.31462 1.58975 2.80018 1.93349 2.36683 2.36683C1.93349 2.80018 1.58975 3.31462 1.35523 3.88081C1.12071 4.447 1 5.05383 1 5.66667Z"
                          stroke="#8F95A2"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="dropdown-block gap-3 d-flex align-items-center position-relative">
                <button
                  className="dropdown-toggle offer-dropdown d-flex justify-content-between align-items-center"
                  onClick={() => setDropdownOpen(prev => !prev)}
                >
                  <div className="ofr-date text-truncate">{selectedSortLabel}</div>
                  <div className="dropdown-polygon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="16" height="16" fill="#FBFCFF" />
                      <path d="M7.99996 12.5602C7.7132 12.5602 7.42649 12.4507 7.20787 12.2322L0.328226 5.35249C-0.109409 4.91486 -0.109409 4.20531 0.328226 3.76785C0.765684 3.33039 1.47509 3.33039 1.91276 3.76785L7.99996 9.8554L14.0872 3.76806C14.5248 3.33061 15.2342 3.33061 15.6716 3.76806C16.1094 4.20552 16.1094 4.91507 15.6716 5.35271L8.79205 12.2324C8.57332 12.451 8.2866 12.5602 7.99996 12.5602Z" fill="#202224" />
                    </svg>

                  </div>
                </button>

                {dropdownOpen && (
                  <ul className="dropdown-menu offer-dropdown-list show" style={{ display: 'block', position: 'absolute' }}>
                    {[
                      { label: "Newest First", sortBy: "createdAt", sortOrder: "DESC" },
                      { label: "Oldest First", sortBy: "createdAt", sortOrder: "ASC" },
                      { label: "Loan Amount High to Low", sortBy: "loanAmount", sortOrder: "DESC" },
                      { label: "Loan Amount Low to High", sortBy: "loanAmount", sortOrder: "ASC" },
                    ].map((item) => (
                      <li key={item.label}>
                        <button
                          className={`dropdown-item ${selectedSortLabel === item.label ? "active" : ""}`}
                          onClick={() => {
                            setParams((prev) => ({
                              ...prev,
                              sortBy: item.sortBy,
                              sortOrder: item.sortOrder,
                            }));
                            setSelectedSortLabel(item.label);
                            setDropdownOpen(false);
                          }}
                        >
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

            </div>
            <div className="active-offers-block d-flex flex-column">
              {isLoading || isFetching ? (
                <div>Loading...</div>
              ) : isError ? (
                <div>Error loading offers.</div>
              ) : offers?.length ? (
                offers.map((offer: any) => (
                  <div key={offer.uuid} className="settlement-box mx-0">
                    <div className="applicant-info">
                      <div className="d-flex align-items-center justify-content-between settlement-block">
                        <div className="applicant-name-bloc d-flex align-items-center">
                          <div className="applicant-name-icon d-flex align-items-center justify-content-center">
                            <span>L</span>
                          </div>
                          <div className="applicant-name d-flex flex-column ms-2">
                            <span className="fw-bold">{offer.lender?.firstName && offer.lender?.lastName ? `${offer.lender.firstName} ${offer.lender.lastName}` : (offer.lender?.fullName || getAnonymousLenderLabel(offer.lender))}</span>
                            {offer.lender?.email && <span className="text-muted small" style={{ fontSize: "12px" }}>{offer.lender.email}</span>}
                          </div>
                        </div>
                        <div className="buttons-borwr-db d-flex align-items-center gap-2">
                          <div onClick={() => navigate("/app/review-offer", { state: { offerId: offer.uuid } })} className="review-btn">
                            <button className="review">Review</button>
                          </div>

                          {!appliedOfferIds.has(offer.uuid) ? (
                            <div className="apply-now">
                              <button
                                className="decline-btn"
                                onClick={() => {
                                  setSelectedOffer(offer);
                                  setIsModalOpen(true);
                                }}
                              >
                                Apply Now
                              </button>
                            </div>
                          ) : (
                            <div className="already-applied">
                              <button className="accept-btn">Already applied</button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="description">
                        <div className="applicant-infofields">
                          Title: <span className="title-db d-inline">{offer.title}</span>
                        </div>
                      </div>

                      <div className="loan-info d-flex align-items-center gap-3 flex-wrap">
                        <div className="applicant-infofields">
                          Loan Amount: <span>£{offer.loanAmount}</span>
                        </div>
                        <div className="vr-line"></div>
                        <div className="applicant-infofields">
                          Interest: <span>{offer.interestRate}%</span>
                        </div>
                        <div className="vr-line"></div>
                        <div className="applicant-infofields">
                          Duration: <span>{offer.loanDuration} months</span>
                        </div>
                        <div className="vr-line"></div>
                        <div className="applicant-infofields">
                          Date:{" "}
                          <span>
                            {new Date(offer?.createdAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                        <div className="vr-line"></div>
                        <div className="applicant-infofields">
                          Loan Duration: <span>{offer.loanDuration} months</span>
                        </div>
                      </div>

                      <div className="description">
                        <div className="applicant-infofields">
                          Description: <span>{offer.termsAndConditions}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data">No open offers found.</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <div className="modal-overlay " >
          <div className="modal-dialog apply-now-modal modal-dialog-centered">
            <div className="modal-content">
              <div className="cross-btn d-flex justify-content-between align-items-center">
                <h1 className="heading m-0" id="applyLabel">Apply now</h1>
                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body p-0 mt-0">
                {selectedOffer && (
                  <>
                    <div className="settlement-box ofr-details-box mt-0 m-0">
                      <h6 className="m-0 settlement-date offer-details-box">Offer Details</h6>

                      <div className="completed-box-review position-relative overflow-hidden">
                        <h4 className="m-0">{selectedOffer.title}</h4>
                        <div className="loan-info d-flex align-items-center gap-3 flex-wrap">
                          <div className="applicant-infofields">
                            Loan Amount: <span>£{selectedOffer.loanAmount}</span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="applicant-infofields">
                            Interest: <span>{selectedOffer.interestRate}%</span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="applicant-infofields">
                            Date:{" "}
                            <span>
                              {new Date(selectedOffer?.createdAt).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="applicant-infofields">
                            Loan Duration: <span>{selectedOffer.loanDuration} months</span>
                          </div>
                        </div>
                        <div className="description">
                          <div className="applicant-infofields">
                            Lender: <span>{selectedOffer?.lender?.firstName && selectedOffer?.lender?.lastName ? `${selectedOffer.lender.firstName} ${selectedOffer.lender.lastName}` : (selectedOffer?.lender?.fullName || getAnonymousLenderLabel(selectedOffer?.lender))}</span>
                          </div>
                        </div>
                        <div className="description">
                          <div className="applicant-infofields">
                            Description: <span>{selectedOffer.termsAndConditions}</span>
                          </div>
                        </div>
                        <div className="grouped-circle position-absolute">
                          <img src={groupedcircles} alt="" className="img-fluid" />
                        </div>
                      </div>
                    </div>

                    <div className="settlement-box your-details-box m-0">
                      <h6 className="m-0 settlement-date">Your Details</h6>
                      <form
                        className="onboarding-form your-details-form"
                        onSubmit={formik.handleSubmit}
                      >
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="firstName" className="form-label">
                              First Name<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.firstName && formik.errors.firstName ? 'is-invalid' : ''}`}
                              id="firstName"
                              {...formik.getFieldProps("firstName")}
                              required
                            />
                            {formik.touched.firstName && formik.errors.firstName && (
                              <div className="invalid-feedback">{formik.errors.firstName as string}</div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="lastName" className="form-label">
                              Last Name<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.lastName && formik.errors.lastName ? 'is-invalid' : ''}`}
                              id="lastName"
                              {...formik.getFieldProps("lastName")}
                              required
                            />
                            {formik.touched.lastName && formik.errors.lastName && (
                              <div className="invalid-feedback">{formik.errors.lastName as string}</div>
                            )}
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="email" className="form-label">
                              Email<span className="text-danger">*</span>
                            </label>
                            <input
                              type="email"
                              className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                              id="email"
                              {...formik.getFieldProps("email")}
                              disabled
                              required
                            />
                            {formik.touched.email && formik.errors.email && (
                              <div className="invalid-feedback">{formik.errors.email as string}</div>
                            )}
                          </div>
                          {/* <div className="col-md-6">
                            <label htmlFor="phoneNumber" className="form-label">
                              Contact Number<span className="text-danger">*</span>
                            </label>
                            <input
                              type="tel"
                              className={`form-control ${formik.touched.phoneNumber && formik.errors.phoneNumber ? 'is-invalid' : ''}`}
                              id="phoneNumber"
                              {...formik.getFieldProps("phoneNumber")}
                              required
                            />
                            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                              <div className="invalid-feedback">{formik.errors.phoneNumber as string}</div>
                            )}
                          </div> */}
                          <div className="col-lg-6 phone-number-label ">
                            <label className="form-label">Phone Number</label>
                            <PhoneInput
                              country="gb" // default country code
                              placeholder="Phone Number"
                              value={formik.values.phoneNumber}
                              onChange={(value) => {
                                // Normalize value with '+' prefix if missing
                                const normalized = value.startsWith('+') ? value : `+${value}`;
                                formik.setFieldValue('phoneNumber', normalized);
                              }}
                              onBlur={() => formik.setFieldTouched('phoneNumber', true)}
                              inputProps={{
                                name: 'phoneNumber',
                                required: true,
                                autoFocus: false,
                              }}
                              inputClass={formik.touched.phoneNumber && formik.errors.phoneNumber ? "input-error" : ""}
                            />
                            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                              <div className="error-message">{formik.errors.phoneNumber as string}</div>
                            )}
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="creditScore" className="form-label">
                              Credit Score
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="creditScore"
                              value={userData?.creditScore || ""}
                              disabled
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="pinCode" className="form-label">
                              Pin code<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.pinCode && formik.errors.pinCode ? 'is-invalid' : ''}`}

                              id="pinCode"
                              {...formik.getFieldProps("pinCode")}
                              required
                            />
                            {formik.touched.pinCode && formik.errors.pinCode && (
                              <div className="invalid-feedback">{formik.errors.pinCode as string}</div>
                            )}
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">
                              Country <span>*</span>
                            </label>
                            <select
                              name="country"
                              className="form-control"
                              value={formik.values.country}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            >
                              <option value="">Select Country</option>
                              {countries.map((country) => (
                                <option key={country.isoCode} value={country.isoCode}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                            {formik.touched.country && formik.errors.country && (
                              <div className="text-danger">{formik.errors.country as string}</div>
                            )}

                          </div>
                          <div className="col-md-6">
                            <label htmlFor="state" className="form-label">
                              State<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.state && formik.errors.state ? 'is-invalid' : ''}`}
                              id="state"
                              {...formik.getFieldProps("state")}
                              required
                            />
                            {formik.touched.state && formik.errors.state && (
                              <div className="invalid-feedback">{formik.errors.state as string}</div>
                            )}
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="city" className="form-label">
                              City<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.city && formik.errors.city ? 'is-invalid' : ''}`}
                              id="city"
                              {...formik.getFieldProps("city")}
                              required
                            />
                            {formik.touched.city && formik.errors.city && (
                              <div className="invalid-feedback">{formik.errors.city as string}</div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="region" className="form-label">
                              Region<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formik.touched.region && formik.errors.region ? 'is-invalid' : ''}`}
                              id="region"
                              {...formik.getFieldProps("region")}
                              required
                            />
                            {formik.touched.region && formik.errors.region && (
                              <div className="invalid-feedback">{formik.errors.region as string}</div>
                            )}
                          </div>
                        </div>
                        <div className="mb-4">
                          <label htmlFor="address" className="form-label">
                            Address<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formik.touched.address && formik.errors.address ? 'is-invalid' : ''}`}
                            id="address"
                            {...formik.getFieldProps("address")}
                            required
                          />
                          {formik.touched.address && formik.errors.address && (
                            <div className="invalid-feedback">{formik.errors.address as string}</div>
                          )}
                        </div>

                        {onboardingUrl && (
                          <div className="alert-box alert-warning mt-4 text-center p-4 m-4">
                            <p className="mb-6">
                              Kindly complete the onboarding process before applying.
                            </p>
                            <div style={{ textAlign: "center", marginTop: "28px" }}>
                              <a style={{ cursor: "pointer" }}
                                onClick={() => {
                                  window.location.href = onboardingUrl; // 👈 open in same tab
                                }}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="button-lender w-100 fw-normal text-decoration-none font-style-italic mt-6"
                              >
                                Complete Stripe Onboarding
                              </a>

                            </div>

                          </div>
                        )}

                        <div className="text-center">
                          <button type="submit" className="btn-apply" disabled={btnLoading} >
                            {btnLoading ? "Submitting..." : "Apply Now"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

  );
}

export default BorrowerDashboard;
